import { jsx } from 'react/jsx-runtime';
import styled from '@emotion/styled';
import { RiCheckboxCircleLine } from '@remixicon/react';
import { space, size } from '../../constants.js';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard.js';
import { useMotionSensor } from '../../hooks/useMotionSensor.js';
import { CopyIcon } from '../../icons/CopyIcon.js';
import { IconButton } from '../IconButton/IconButton.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { TextLinkButton } from '../TextLink/TextLink.js';

const CopyToClipboardButton = ({ ButtonComponent = TextLinkButton, "aria-label": ariaLabel, children, copiedMessage = "Copied!", copyMessage = "Copy to clipboard", tooltipProps = {}, value, ...restProps })=>{
    const [showCopiedMessage, triggerCopiedMessage, reset] = useMotionSensor(2000);
    const [_, copy] = useCopyToClipboard();
    const onCopy = async (e)=>{
        void copy(typeof value === "function" ? await value() : value);
        triggerCopiedMessage();
        e.stopPropagation();
        e.preventDefault();
    };
    const message = showCopiedMessage ? copiedMessage : copyMessage;
    return jsx(Tooltip, {
        content: message,
        cssForTooltipWrapper: {
            display: "inline-flex",
            ...restProps.cssForTooltipWrapper
        },
        onHide: ()=>{
            reset();
        },
        ...tooltipProps,
        delay: (tooltipProps === null || tooltipProps === void 0 ? void 0 : tooltipProps.delay) || [
            0,
            500
        ],
        children: children ? jsx(ButtonComponent, {
            "aria-label": getButtonLabel({
                copyMessage,
                providedLabel: ariaLabel
            }),
            ...restProps,
            leadingIcon: showCopiedMessage && restProps.leadingIcon ? jsx(RiCheckboxCircleLine, {
                color: restProps.color,
                size: size.base
            }) : restProps.leadingIcon,
            onClick: onCopy,
            style: {
                alignItems: showCopiedMessage ? "center" : undefined
            },
            children: children
        }) : jsx(SIconButton, {
            "aria-label": getButtonLabel({
                copyMessage,
                providedLabel: ariaLabel
            }),
            icon: showCopiedMessage ? jsx(RiCheckboxCircleLine, {
                color: restProps.color,
                size: size.base
            }) : jsx(CopyIcon, {
                color: restProps.color,
                fixedSize: 14
            }),
            id: "copy-to-clipboard-icon",
            onClick: onCopy,
            prominence: "low",
            ...restProps
        })
    });
};
const getButtonLabel = ({ copyMessage, providedLabel })=>{
    if (providedLabel) {
        return providedLabel;
    }
    if (typeof copyMessage === "string") {
        return copyMessage;
    }
    return "Copy to clipboard";
};
const SIconButton = styled(IconButton)({
    minHeight: "fit-content",
    minWidth: "fit-content",
    padding: space.s1,
    borderRadius: space.s1
});

export { CopyToClipboardButton };
